import { useEffect, useState } from "react";
import { api, type ApiError } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { LoginFrame } from "@/ui/login-frame";
import type { LoginLook } from "@/lib/brand";

/**
 * Login em até 3 etapas: email+senha → código 2FA → escolha do escritório.
 * Entre etapas o servidor devolve um `ticket` assinado (5 min); o navegador
 * nunca envia o ID do usuário.
 */
type Step = "credentials" | "mfa" | "tenant" | "forgot" | "forgot_sent";

interface LoginResp {
  requires: "mfa" | "tenant_selection" | "none";
  ticket?: string;
  tenants?: Array<{ tenantId: string; slug: string; displayName: string }>;
  session?: { accessToken: string; refreshToken: string; expiresAt: string };
}

interface PublicBrand { officeName: string; brandColor?: string | null; loginBg?: string | null; loginText?: string | null; hasLogo?: boolean; hasBgLogo?: boolean; hasBgImage?: boolean; hasFavicon?: boolean; v?: number; login?: LoginLook | null }

/** Chrome/Edge: guarda a senha no gerenciador do navegador depois do login (Safari usa o envio do formulário). */
function storeCredential(email: string, password: string): void {
  try {
    const W = window as unknown as { PasswordCredential?: new (d: { id: string; password: string }) => Credential };
    if (W.PasswordCredential && navigator.credentials?.store) void navigator.credentials.store(new W.PasswordCredential({ id: email, password })).catch(() => undefined);
  } catch { /* sem suporte */ }
}

export function LoginPage(): JSX.Element {
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState<string | null>(null);
  const [tenants, setTenants] = useState<LoginResp["tenants"]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setSession = useAuth((s) => s.setSession);
  const [brand, setBrand] = useState<PublicBrand | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/v1/publico/marca").then((r) => (r.ok ? r.json() : null)).then((b: PublicBrand | null) => {
      if (!alive || !b) return;
      setBrand(b);
      document.title = b.officeName || "VG APP";
      if (b.hasFavicon) { const l = document.querySelector<HTMLLinkElement>("link[rel='icon']"); if (l) { l.type = "image/png"; l.href = `/api/v1/publico/favicon?v=${b.v ?? ""}`; } }
    }).catch(() => undefined);
    return () => { alive = false; };
  }, []);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      await fn();
    } catch (err) {
      const e = err as ApiError;
      // Ticket expirado: volta ao início.
      if (e.status === 401 && (step === "mfa" || step === "tenant")) {
        setStep("credentials");
        setTicket(null);
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handle = (r: LoginResp): void => {
    if (r.ticket) setTicket(r.ticket);
    if (r.requires === "mfa") { setCode(""); setStep("mfa"); return; }
    if (r.requires === "tenant_selection") {
      setTenants(r.tenants ?? []);
      setStep("tenant");
      return;
    }
    if (r.session) {
      if (password) storeCredential(email, password);
      setSession(r.session);
    }
  };

  return (
    <LoginFrame bg={brand?.loginBg} look={brand?.login} brandColor={brand?.brandColor} officeName={brand?.officeName ?? "VG APP"} note={brand?.loginText}
      bgLogoUrl={brand?.hasBgLogo ? `/api/v1/publico/logo?fundo=1&v=${brand.v ?? ""}` : null}
      bgImageUrl={brand?.hasBgImage ? `/api/v1/publico/login-fundo?v=${brand.v ?? ""}${brand.login?.bgMode === "mosaico" ? "&logo=1" : ""}` : null}
      cardLogoUrl={brand?.hasLogo ? `/api/v1/publico/logo?v=${brand.v ?? ""}` : null}>
        {!brand?.hasLogo && (brand?.login?.cardLogo ?? true) && <div className="login-logo" style={brand?.brandColor ? { background: brand.brandColor } : undefined}>{(brand?.officeName ?? "VG").split(/\s+/).filter((p) => p.length > 2).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "VG"}</div>}

        {step === "credentials" && (
          <form
            className="login-form" method="post" action="/login" id="login-form" autoComplete="on"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => handle(await api.post<LoginResp>("/auth/login", { email, password, remember })));
            }}
          >
            {/* name/id/autocomplete no padrão que o app Senhas, Safari, Chrome e 1Password reconhecem. */}
            <label htmlFor="username">
              E-mail
              <input id="username" name="username" type="email" inputMode="email" required autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
            </label>
            <label htmlFor="password">
              Senha
              <input id="password" name="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
            </label>
            <label className="check" style={{ flexDirection: "row", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={loading} />
              Manter conectado neste dispositivo por 15 dias
            </label>
            <div className="login-hint" style={{ textAlign: "left", fontSize: 12 }}>
              {remember ? "Use só em computador seu: por 15 dias você não precisa entrar de novo nem digitar o código de 2 etapas neste navegador." : "Sem marcar: a sessão acaba após 4h sem uso ou 20h no máximo."}
            </div>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? "Entrando..." : "Entrar"}
            </button>
            <button type="button" className="link-btn"
              onClick={() => { setError(null); setStep("forgot"); }}>
              Esqueci a senha
            </button>
          </form>
        )}

        {step === "mfa" && (
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () =>
                handle(await api.post<LoginResp>("/auth/login/mfa", { ticket, code, remember })));
            }}
          >
            <input type="text" name="username" autoComplete="username" value={email} readOnly hidden />
            <div className="login-hint">
              Digite o código de 6 dígitos do seu aplicativo autenticador,
              ou um código de backup.{remember && " Este navegador fica confiável por 15 dias."}
            </div>
            <label>
              Código
              <input type="text" required autoComplete="one-time-code" autoFocus
                maxLength={16} value={code} onChange={(e) => setCode(e.target.value)}
                disabled={loading} />
            </label>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="btn primary" disabled={loading || code.trim().length < 6}>
              {loading ? "Verificando..." : "Verificar"}
            </button>
            <button type="button" className="link-btn" onClick={() => setStep("credentials")}>
              Voltar
            </button>
          </form>
        )}

        {step === "tenant" && (
          <div className="login-form">
            <div className="login-hint">Escolha o escritório</div>
            <div className="tenant-list">
              {tenants?.map((t) => (
                <button key={t.tenantId} type="button" className="tenant-card" disabled={loading}
                  onClick={() => void run(async () =>
                    handle(await api.post<LoginResp>("/auth/select-tenant", { ticket, tenantId: t.tenantId, remember })))}>
                  <div className="tenant-name">{t.displayName}</div>
                  <div className="tenant-slug">{t.slug}</div>
                </button>
              ))}
            </div>
            {error && <div className="error">{error}</div>}
          </div>
        )}

        {step === "forgot" && (
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await api.post("/auth/password/forgot", { email });
                setStep("forgot_sent");
              });
            }}
          >
            <div className="login-hint">
              Informe seu e-mail. Enviaremos um link para criar uma nova senha.
            </div>
            <label>
              E-mail
              <input type="email" required autoComplete="email" autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
            </label>
            {error && <div className="error">{error}</div>}
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? "Enviando..." : "Enviar link"}
            </button>
            <button type="button" className="link-btn" onClick={() => setStep("credentials")}>
              Voltar
            </button>
          </form>
        )}

        {step === "forgot_sent" && (
          <div className="login-form">
            <div className="success">
              Se houver uma conta com esse e-mail, o link chega em alguns instantes.
              Ele vale por 30 minutos.
            </div>
            {import.meta.env.DEV && (
              <div className="login-hint">
                Em desenvolvimento, o e-mail aparece no Mailpit:{" "}
                <a href="http://localhost:8025" target="_blank" rel="noreferrer">localhost:8025</a>
              </div>
            )}
            <button type="button" className="btn" onClick={() => setStep("credentials")}>
              Voltar ao login
            </button>
          </div>
        )}
    </LoginFrame>
  );
}
